import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { isValidImageFile } from 'utils/file';

export interface AvatarUploadHandlers {
  /**
   * Uploads an image that already passed the client-side checks. Reject to
   * signal failure; reporting it (toast etc.) is the caller's job.
   */
  onUpload: (file: File) => Promise<unknown>;
  /** Removes the current avatar. Reject to signal failure. */
  onRemove: () => Promise<unknown>;
}

/**
 * State and handlers behind an avatar picker: a hidden file input, the
 * PNG/JPEG + 2 MB checks from `utils/file`, and busy flags for upload/remove.
 * Where the file goes is up to the caller (own profile vs. admin editing
 * another user hit different endpoints).
 */
export const useAvatarUploadField = ({ onUpload, onRemove }: AvatarUploadHandlers) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      const validity = isValidImageFile(file);
      if (validity !== true) {
        setValidationError(validity);
        return;
      }

      setValidationError(null);
      setIsUploading(true);
      try {
        await onUpload(file);
      } catch {
        // The caller reports the failure; only the busy state is ours.
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload]
  );

  const handleFileChange = useCallback(
    async (event: Pick<React.ChangeEvent<HTMLInputElement>, 'currentTarget'>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      // Reset so picking the same file again still fires a change event.
      input.value = '';
      if (!file) return;
      await uploadFile(file);
    },
    [uploadFile]
  );

  const handleRemove = useCallback(async () => {
    setValidationError(null);
    setIsRemoving(true);
    try {
      await onRemove();
    } catch {
      // The caller reports the failure; only the busy state is ours.
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove]);

  return {
    fileInputRef,
    isUploading,
    isRemoving,
    validationError,
    openFilePicker,
    handleFileChange,
    handleRemove,
  };
};
